// src/pages/PrivacyPolicy.tsx
// App Store-grade privacy policy covering camera, AR, analytics, third parties,
// and data deletion rights — effective May 2026. Updated Oct 2026 for the
// instant in-app account deletion (supabase/functions/delete-account) and the
// language / country / units preferences the mobile app stores on the profile.
// Oct 30 2026: factual corrections only, to match what the app does (camera uploads in
// Redesign / Send to Store, anonymous guest session, deletion list, processor rows).
// Structure unchanged: English text, with the FR/ES "English only" banner.

import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

function H2({ children }: { children: React.ReactNode }) {
  return <h2 className="text-xl font-bold text-white mt-10 mb-3">{children}</h2>;
}
function H3({ children }: { children: React.ReactNode }) {
  return <h3 className="text-base font-semibold text-gray-200 mt-5 mb-2">{children}</h3>;
}
function P({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <p className={`text-gray-300 leading-relaxed ${className}`}>{children}</p>;
}
function Li({ children }: { children: React.ReactNode }) {
  return <li className="text-gray-300 leading-relaxed">{children}</li>;
}

export default function PrivacyPolicy() {
  const { pathname } = useLocation();
  const locale = pathname.startsWith("/fr") ? "fr" : pathname.startsWith("/es") ? "es" : "en";

  const contactEmail = "charles@fyrris.com";
  // Portal / business-account deletion and other privacy requests.
  const privacyEmail = "privacy@fyrris.com";

  // The policy BODY is intentionally English-only (see the notice below), but
  // /fr/confidentialite and /es/privacidad are declared as the French and
  // Spanish alternates in the hreflang cluster. Serving them an English <title>
  // made them read as English results in French and Spanish SERPs, so the head
  // is localized even though the document is not.
  const meta = {
    en: {
      title: "Privacy Policy — Fyrris",
      description: "How Fyrris collects, uses, and protects your data in the Fyrris app and website.",
    },
    fr: {
      title: "Politique de confidentialité — Fyrris",
      description: "Comment Fyrris recueille, utilise et protège vos données dans l'application et le site Fyrris.",
    },
    es: {
      title: "Política de privacidad — Fyrris",
      description: "Cómo Fyrris recopila, usa y protege tus datos en la aplicación y el sitio web de Fyrris.",
    },
  }[locale];

  return (
    <>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta property="og:title"       content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image"       content="https://www.fyrris.com/fyrris-social-banner.png" />
        <meta property="og:type"        content="website" />
      </Helmet>

      {locale !== "en" && (
        <div className="bg-gray-800 border-b border-gray-700 px-6 py-3 text-sm text-gray-400 text-center">
          {locale === "fr"
            ? "Ce document est disponible en anglais uniquement. Veuillez nous écrire à "
            : "Este documento está disponible solo en inglés. Por favor contáctenos en "}
          <a href={`mailto:${contactEmail}`} className="text-amber-400 underline">{contactEmail}</a>
          {locale === "fr" ? " pour une version traduite." : " para una versión traducida."}
        </div>
      )}

      <div className="min-h-screen bg-gray-900 text-gray-200 px-6 py-16 md:px-20">
        <div className="max-w-3xl mx-auto">

          {/* ── Header ──────────────────────────────────────────────────── */}
          <div className="mb-10 pb-8 border-b border-gray-700">
            <p className="text-xs font-semibold uppercase tracking-widest text-amber-400 mb-3">Legal</p>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-sm text-gray-500">
              Effective Date: May 1, 2026 · Last Updated: October 30, 2026
            </p>
          </div>

          {/* ── 1. Introduction ────────────────────────────────────────── */}
          <H2>1. Introduction</H2>
          <P>
            16349684 Canada Inc. (doing business as <strong className="text-white">Fyrris</strong>) and
            Fyrris Inc. ("Fyrris," "we," "our," or "us") respect your privacy. This Privacy Policy
            explains what information we collect when you use the Fyrris mobile application
            ("App") and website at fyrris.com ("Site"), how we use it, and the choices you have.
          </P>
          <P className="mt-3">
            By using the App or Site, you agree to the collection and use of information described
            in this policy. If you do not agree, please do not use the App or Site.
          </P>

          {/* ── 2. Data collected ──────────────────────────────────────── */}
          <H2>2. Information We Collect</H2>

          <H3>2.1 Account Information</H3>
          <P>
            If you create a retailer account, we collect your name, email address, business name,
            store locations, and payment information (processed by our payment provider — we do
            not store raw card numbers).
          </P>

          <H3>2.2 Guest Session and Optional Consumer Account</H3>
          <P>
            You can use the App without an account. The first time you open it, the App creates an
            anonymous guest session with our authentication provider (Supabase) so it can talk to
            our servers. The guest session has a random identifier and no email address, name or
            password. It is kept on your device and is what lets features such as AI
            recommendations and Redesign My Space work without an account. In Profile you can
            choose <strong className="text-white">Clear My Data</strong> to permanently delete the
            Redesign photos and results, store requests and their photos, store pairings and
            favourites stored for the guest session, and start a fresh one.
          </P>
          <P className="mt-3">
            If you choose to create an account in the App, your guest session is upgraded in place
            and keeps the same internal identifier, so data already stored for it now belongs to
            your account. We store your email address (for sign-in), an optional display name, your
            device's push notification token if you allow notifications, and the following app
            preferences so they follow you to a new device:
          </P>
          <ul className="list-disc list-inside space-y-1.5 mt-2 ml-2">
            <Li><strong className="text-white">Language</strong> (English or French, or "automatic" to follow your device)</Li>
            <Li><strong className="text-white">Country</strong> (Canada or the United States, or "automatic"), which sets the products and retailers shown and the clearance standard used in AR</Li>
            <Li><strong className="text-white">Measurement units</strong> (metric or imperial, or "automatic")</Li>
          </ul>
          <P className="mt-3">
            Without an account, these preferences are stored only on your device. You can delete
            your account at any time from the App (see Section 5).
          </P>

          <H3>2.3 Consumer App Usage</H3>
          <P>
            Consumer users of the Fyrris mobile app do not need to create an account to browse
            products or use AR. We collect:
          </P>
          <ul className="list-disc list-inside space-y-1.5 mt-2 ml-2">
            <Li>Anonymized usage events (which products were viewed, AR sessions started) — no names or identifiers</Li>
            <Li>Device type and OS version, for compatibility diagnostics only</Li>
            <Li>App Store country code, for regional pricing display</Li>
            <Li>Onboarding preferences you enter (interests, style, budget) — stored locally on your device using AsyncStorage, and also sent to our servers (and from there to our AI provider, Anthropic) to generate your personalized product recommendations. This data is not linked to your name or email — we do not collect either from consumer app users — but it is more than an anonymized identifier</Li>
          </ul>

          <H3>2.4 Camera Access</H3>
          <P>
            The Fyrris App requests access to your device camera <strong className="text-white">to
            power augmented reality (AR) product visualization</strong>. Specifically:
          </P>
          <ul className="list-disc list-inside space-y-1.5 mt-2 ml-2">
            <Li>Camera frames are processed on your device by ARKit (iOS) to detect surfaces and place 3D models. During normal AR use they are not sent anywhere</Li>
            <Li>No photos are captured, stored, or shared by the Fyrris App without your explicit action</Li>
            <Li>Camera access is used only while the AR viewer is active; it is released when you exit AR</Li>
          </ul>
          <P className="mt-3">
            Where these features are available in your version of the App, two buttons in the AR
            viewer upload a single capture of your view. Nothing is uploaded unless you tap one of them:
          </P>
          <ul className="list-disc list-inside space-y-1.5 mt-2 ml-2">
            <Li><strong className="text-white">Redesign My Space:</strong> one capture of your AR view (and, on supported devices, a depth map) is uploaded to Fyrris and sent to Stability AI to generate restyled images of your room. The first time, the App shows an AI disclosure and asks for your consent. If you tap "Not now", nothing is uploaded and the rest of the App keeps working. You can withdraw your consent in Profile → Privacy</Li>
            <Li><strong className="text-white">Send to Store:</strong> after you pair with a participating retailer, one capture of your AR view, together with the clearance check result for the product, is uploaded and shared with that retailer so they can prepare a quote</Li>
            <Li>These captures and the images generated from them are deleted when you delete your account or use Clear My Data (Section 5.1)</Li>
          </ul>

          <H3>2.5 Location Data</H3>
          <P>
            If you grant location permission, we use your approximate location to show nearby
            retailers on the map feature. The App may also use that same location, on your
            device, to work out which country you are in and suggest switching your country
            setting. It always asks before changing anything. Only the two-letter country code
            is kept, on your device. We do not store your precise GPS coordinates on our
            servers. Location data is used only in-session and is not shared with third parties.
            You can deny or revoke location permission at any time in your device Settings; the
            App then falls back to your device's region setting.
          </P>

          <H3>2.6 Photo Library</H3>
          <P>
            If you choose to save an AR screenshot, the Fyrris App requests access to your photo
            library to save that image. We do not read, scan, or upload any photos from your library.
          </P>

          <H3>2.7 Retailer Attribution &amp; Deep Links</H3>
          <P>
            If you arrive at the App or Site via a retailer-specific link, we record which retailer
            referred you (a retailer ID) to display the correct retailer branding. This is stored
            anonymously and not linked to your personal identity.
          </P>

          {/* ── 3. How we use ──────────────────────────────────────────── */}
          <H2>3. How We Use Your Information</H2>
          <ul className="list-disc list-inside space-y-1.5 ml-2">
            <Li>To deliver and improve the App and Site</Li>
            <Li>To generate AI-powered product recommendations (using the preferences you enter, not your name or email)</Li>
            <Li>To display nearby retailers on the map</Li>
            <Li>To communicate important updates, billing information, and support responses (retailers only)</Li>
            <Li>To contact retailers and manufacturers about Fyrris using business contact information they have published or shared with us. Every such email identifies Fyrris and lets you opt out, and we stop contacting you once you do</Li>
            <Li>To detect and fix technical issues and prevent fraud</Li>
            <Li>To comply with legal obligations</Li>
          </ul>
          <P className="mt-3">
            We do not use your personal data for advertising, sell it to data brokers, or share it
            with third parties for their own marketing purposes.
          </P>

          {/* ── 4. Analytics & Cookies ─────────────────────────────────── */}
          <H2>4. Analytics &amp; Cookies</H2>

          <H3>4.1 Analytics</H3>
          <P>
            We collect <strong className="text-white">anonymous usage events</strong> to understand how
            the App is used and to improve it. These events contain no names or email addresses.
            They are associated with a randomly generated identifier tied to your device, which
            persists across app sessions but is not linked to your name, email, or any other way
            of identifying you personally. Examples include: "AR session started for category:
            fireplaces" or "product detail viewed." We use Vercel Analytics for website traffic
            (see Section 6).
          </P>

          <H3>4.2 Cookies</H3>
          <P>
            The Site (fyrris.com) uses a small number of cookies and shows a cookie banner on
            your first visit so you can accept or decline non-essential cookies. Some cookies are
            required for the Site to function (for example, keeping you signed in to the retailer
            portal) and are not affected by this choice. Vercel Analytics, which we use for
            website traffic analytics, does not use cookies. The Fyrris mobile App does not use
            cookies.
          </P>

          {/* ── 5. Data retention ──────────────────────────────────────── */}
          <H2>5. Data Retention</H2>
          <P>
            Account data is retained for as long as your account is active. Anonymous analytics
            events are retained for up to 24 months. AR placement data stored on your device
            (camera orbit position) is kept for 7 days and then automatically deleted.
          </P>

          <H3>5.1 Deleting Your App Account</H3>
          <P>
            You can delete your consumer account yourself, at any time, in the App under{" "}
            <strong className="text-white">Profile → Delete Account</strong>. Deletion happens
            immediately. You don't need to contact us, and it cannot be undone.
          </P>
          <P className="mt-3">Permanently deleted:</P>
          <ul className="list-disc list-inside space-y-1.5 mt-2 ml-2">
            <Li>Your sign-in account (email address and password)</Li>
            <Li>Your profile: display name, push notification token, and your language, country and measurement-unit preferences</Li>
            <Li>Your store pairings and the AR store-quote requests you sent to retailers, including the photos attached to them</Li>
            <Li>Your AR Redesign history and usage records, the room photos you uploaded, and the redesign images generated from them</Li>
            <Li>Usage events linked to your account or guest session, if any</Li>
          </ul>
          <P className="mt-3">Kept, but disconnected from you (anonymized):</P>
          <ul className="list-disc list-inside space-y-1.5 mt-2 ml-2">
            <Li>Retailer attribution records from links you opened. Your account is removed from them, and they remain only as aggregate referral counts for retailers</Li>
            <Li>Anonymous usage events, which never contained your name, email or account in the first place</Li>
          </ul>
          <P className="mt-3">
            These photo and image files are deleted automatically as part of the same step, before
            your account itself is removed. If any part of the deletion fails, nothing is deleted
            and the App asks you to try again, so your account is never left half-deleted.
            Favourites and recently viewed products saved on your device are cleared at the same
            time; other preferences saved on your device are removed when you delete the App.
          </P>

          <H3>5.2 Retailer, Manufacturer and Other Business Accounts</H3>
          <P>
            Portal and business accounts (retailers, manufacturers and administrators) can't be
            deleted from the App, because they are linked to business data such as store
            listings, products and billing. To close one, email{" "}
            <a href={`mailto:${privacyEmail}`} className="text-amber-400 underline">{privacyEmail}</a>{" "}
            from the account's email address. We will confirm when it's done, except where we are
            required by law to retain certain data longer (for example, billing records).
          </P>

          {/* ── 6. Third-party services ────────────────────────────────── */}
          <H2>6. Third-Party Services</H2>
          <P>We use the following third-party services to operate Fyrris:</P>

          <div className="mt-4 rounded-xl border border-gray-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-800">
                <tr>
                  <th className="text-left px-4 py-2.5 text-gray-300 font-semibold">Service</th>
                  <th className="text-left px-4 py-2.5 text-gray-300 font-semibold">Purpose</th>
                  <th className="text-left px-4 py-2.5 text-gray-300 font-semibold">Data sent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {[
                  ["Supabase", "Database and user authentication", "Account data and anonymous guest sessions; usage events"],
                  ["Cloudflare R2", "3D model and asset delivery (CDN); storage of captures you choose to upload", "Asset files; the captures you upload with Redesign My Space or Send to Store"],
                  ["Anthropic (Claude AI)", "AI spec extraction from manufacturer PDFs; AI product recommendations", "Product spec documents; consumer onboarding preferences (interests, style, budget — not linked to name or email)"],
                  ["Vercel Analytics", "Website traffic analytics (anonymized)", "Page views, referrer, device type (no cookies)"],
                  ["Stripe", "Payment processing for retailer subscriptions", "Billing name, email, card tokens — no raw card numbers stored by Fyrris"],
                  ["AWS (Lambda, SQS)", "3D asset processing pipeline (infrastructure)", "Product asset files and processing metadata; consumer-submitted room photos for the AR Redesign feature"],
                  ["HubSpot", "CRM for retailer/manufacturer leads and accounts", "Name, email, phone, company name for retailers and manufacturers (not consumer app users)"],
                  ["Resend", "Transactional and account-related emails; business outreach emails to retailers and manufacturers", "Name, email address and business name, for retailers and manufacturers"],
                  ["Cal.com", "Meeting scheduling for retailer demos and onboarding", "Name, email, and meeting time — collected directly by Cal.com, not by Fyrris"],
                  ["Sentry", "Error and crash monitoring", "In the App: crash reports and error diagnostics (device model, OS and App version, and the screen or product involved). We do not set your name, email or account identifier on these reports. On the website: device/crash diagnostic data and a recording of on-screen activity (\"session replay\") when an error occurs"],
                  ["Replicate", "3D asset generation from product images", "Product images only — no user data"],
                  ["Stability AI", "3D texture generation; AI room-redesign image generation", "Product images; for Redesign My Space, one capture of your AR view (and a depth map on supported devices) that you choose to upload, after you give consent in the App"],
                  ["Expo", "Mobile push notification delivery", "Device push token"],
                  ["Slack", "Internal team notifications", "Retailer and product names, for our own operational alerts — not customer-facing"],
                ].map(([svc, purpose, data]) => (
                  <tr key={svc} className="bg-gray-900/60">
                    <td className="px-4 py-2.5 text-white font-medium">{svc}</td>
                    <td className="px-4 py-2.5 text-gray-400">{purpose}</td>
                    <td className="px-4 py-2.5 text-gray-400">{data}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <P className="mt-4">
            Each of these providers has its own privacy policy. We encourage you to review them.
            We do not authorize these providers to use your data for any purpose other than
            providing their services to us.
          </P>

          {/* ── 7. Children ────────────────────────────────────────────── */}
          <H2>7. Children's Privacy</H2>
          <P>
            The Fyrris App is rated 4+ on the App Store and does not knowingly collect personal
            information from children under 13 (or under 16 in the EU/UK). If you believe a child
            has provided us with personal data, please contact us at{" "}
            <a href={`mailto:${contactEmail}`} className="text-amber-400 underline">{contactEmail}</a>{" "}
            and we will delete it promptly.
          </P>

          {/* ── 8. Your rights ─────────────────────────────────────────── */}
          <H2>8. Your Rights</H2>
          <P>Depending on where you are located, you may have the right to:</P>
          <ul className="list-disc list-inside space-y-1.5 mt-2 ml-2">
            <Li><strong className="text-white">Access</strong> the personal data we hold about you</Li>
            <Li><strong className="text-white">Correct</strong> inaccurate personal data</Li>
            <Li><strong className="text-white">Delete</strong> your personal data ("right to be forgotten")</Li>
            <Li><strong className="text-white">Port</strong> your data to another service (EU/Quebec residents)</Li>
            <Li><strong className="text-white">Object</strong> to or restrict certain types of processing</Li>
            <Li><strong className="text-white">Withdraw consent</strong> where processing is based on consent</Li>
          </ul>
          <P className="mt-3">
            App users can delete their account instantly in the App (Section 5.1). To exercise
            any other right, or to close a business account, email{" "}
            <a href={`mailto:${privacyEmail}`} className="text-amber-400 underline">{privacyEmail}</a>
            {" "}with the subject line "Privacy Request." We will respond within 30 days and confirm
            with you once your request has been completed.
          </P>

          {/* ── 9. Security ────────────────────────────────────────────── */}
          <H2>9. Security</H2>
          <P>
            We use industry-standard security measures including TLS encryption in transit, encrypted
            database storage, and role-based access controls. However, no system is completely secure.
            If you discover a security vulnerability, please email{" "}
            <a href={`mailto:${contactEmail}`} className="text-amber-400 underline">{contactEmail}</a>.
          </P>

          {/* ── 10. Changes ────────────────────────────────────────────── */}
          <H2>10. Changes to This Policy</H2>
          <P>
            We may update this Privacy Policy. If we make material changes, we will notify you by
            email (for account holders) and by updating the "Last Updated" date above. Continued
            use of the App or Site after changes take effect constitutes acceptance of the revised policy.
          </P>

          {/* ── 11. Contact ────────────────────────────────────────────── */}
          <H2>11. Contact Us</H2>
          <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-5 space-y-1 text-sm text-gray-300">
            <p className="font-semibold text-white">Charles Dupuy, Founder</p>
            <p className="text-gray-500">Person in charge of the protection of personal information</p>
            <p>Email: <a href={`mailto:${contactEmail}`} className="text-amber-400 underline">{contactEmail}</a></p>
            <p>Privacy requests: <a href={`mailto:${privacyEmail}`} className="text-amber-400 underline">{privacyEmail}</a></p>
            <div className="pt-3 mt-3 border-t border-gray-800 space-y-3">
              <div>
                <p className="font-medium text-gray-300">16349684 Canada Inc. (dba Fyrris)</p>
                <p>23 Rue des Lilas, Bromont, QC J2L 1M4, Canada</p>
              </div>
              <div>
                <p className="font-medium text-gray-300">Fyrris Inc.</p>
                <p>911 N Buffalo Dr, Suite 202, Las Vegas, NV 89128, USA</p>
              </div>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-gray-800 text-xs text-gray-600 text-center">
            <p>© 2026 Fyrris. All rights reserved.</p>
          </div>

        </div>
      </div>
    </>
  );
}
